import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { submitContact } from "../lib/api";
import { ArrowRight, X } from "lucide-react";

const initial = { name: "", email: "", company: "", message: "" };

export default function ContactDialog({ open, onOpenChange, interest }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset after close transition
      const t = setTimeout(() => {
        setForm(initial);
        setErrors({});
        setSuccess(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please add your name.";
    if (!form.email.trim()) e.email = "We need your email to reply.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "That email doesn't look right.";
    if (!form.message.trim()) e.message = "Tell us a little about your business.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
        interest: interest || null,
      });
      setSuccess(true);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email hello@allwedoiswrite.com.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const headline =
    interest === "audit"
      ? "Book a Communications Audit"
      : interest === "partner"
      ? "Become a Communications Partner"
      : "Let's Talk About Growth";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="contact-dialog"
        className="awd-sharp max-w-2xl bg-[#F9F6F0] border border-[#E2DCD0] p-0 overflow-hidden gap-0"
      >
        <button
          onClick={() => onOpenChange(false)}
          data-testid="contact-dialog-close"
          aria-label="Close"
          className="absolute right-4 top-4 z-10 w-9 h-9 inline-flex items-center justify-center text-[#0F172A] hover:bg-[#F2EFEB] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {success ? (
          <div className="p-8 md:p-12 text-center" data-testid="contact-success">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C86A53] mb-6">
              Message Received
            </p>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight">
              Thank you — your <span className="italic">story</span> is on its way.
            </h3>
            <p className="mt-4 text-[#475569] font-light leading-relaxed">
              Rachel will personally read your message and reply within a couple of working days.
            </p>
            <button
              data-testid="contact-success-close"
              onClick={() => onOpenChange(false)}
              className="mt-8 inline-flex items-center gap-3 bg-[#0F172A] hover:bg-[#C86A53] text-[#F9F6F0] px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
            >
              Close
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-8 md:px-12 pt-10 md:pt-12 pb-2 text-left">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C86A53] mb-3">
                Start the conversation
              </p>
              <DialogTitle
                data-testid="contact-dialog-title"
                className="font-display font-light text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight"
              >
                {headline}
              </DialogTitle>
              <DialogDescription className="text-[#475569] font-light text-base leading-relaxed mt-2">
                Tell me about your business and what you're hoping to achieve.
              </DialogDescription>
            </DialogHeader>

            <form noValidate onSubmit={onSubmit} className="px-8 md:px-12 pb-10 md:pb-12 pt-6 space-y-6">
              <Field
                id="name"
                label="Your name"
                value={form.name}
                onChange={setField("name")}
                error={errors.name}
                testId="contact-name"
                autoComplete="name"
              />
              <Field
                id="email"
                label="Email"
                type="email"
                value={form.email}
                onChange={setField("email")}
                error={errors.email}
                testId="contact-email"
                autoComplete="email"
              />
              <Field
                id="company"
                label="Company (optional)"
                value={form.company}
                onChange={setField("company")}
                testId="contact-company"
                autoComplete="organization"
              />
              <div>
                <label
                  htmlFor="message"
                  className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0F172A] mb-2"
                >
                  What's on your mind?
                </label>
                <textarea
                  id="message"
                  data-testid="contact-message"
                  rows={4}
                  value={form.message}
                  onChange={setField("message")}
                  className="w-full bg-transparent border-b border-[#0F172A]/30 focus:border-[#C86A53] outline-none py-3 text-[#0F172A] font-light text-base placeholder:text-[#475569]/60 transition-colors resize-none"
                  placeholder="A few lines about your business and where you'd like help..."
                />
                {errors.message && (
                  <p className="mt-2 text-xs text-[#C86A53]" data-testid="error-message">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="pt-2 flex items-center justify-between gap-4">
                <p className="text-xs text-[#475569] italic">
                  We'll never share your details.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 bg-[#C86A53] hover:bg-[#A65541] disabled:opacity-60 disabled:cursor-not-allowed text-[#F9F6F0] px-7 py-3.5 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  {!submitting && (
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, type = "text", value, onChange, error, testId, autoComplete }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0F172A] mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-[#0F172A]/30 focus:border-[#C86A53] outline-none py-3 text-[#0F172A] font-light text-base placeholder:text-[#475569]/60 transition-colors"
      />
      {error && (
        <p className="mt-2 text-xs text-[#C86A53]" data-testid={`error-${id}`}>
          {error}
        </p>
      )}
    </div>
  );
}
